const act = async (b, fn, done) => {
  setBusy(b.id, true);
  errors.value = {};
  try {
    const res = await fn();
    setAlertMessage(res?.message || done);<script setup>
// My bookings (BOOKINGS_PLAN.md B14, BE22; AppBookMine): coming up and past,
// match bookings tagged, Cancel with the cut-off words (a refund before it,
// none after), the one-tap Release, and Pay now for a booking still waiting
// on a card. Used by the profile tab (every club) and the club page (one club).
// Grant 2026-11-02: a row opens the booking's own page (/your-profile/bookings/{id});
// the buttons beside it still act here; the pay dialog is BookingPayDialog.
import {computed, onMounted, ref, watch} from 'vue';
import {useBookingsMember, bookingApiError} from '~/composables/useBookingsMember';
import {
  BOOKING_ICON, bookingKindChip, bookingStatusChip, cancelWords, money, paidWords, personalBookings, releaseWords, splitBookings, viewerOwes, withLine,
} from '~/utils/bookingsMember';
import {bookingDetailPath} from '~/utils/bookingDetail';
import BookingPayDialog from '~/components/bookings/BookingPayDialog.vue';

const props = defineProps({
  clubId: {type: String, default: null},
  /** Show the club's name on each row (the profile tab). */
  showClub: {type: Boolean, default: false},
  /** Offer the "Past" section. */
  withPast: {type: Boolean, default: true},
  /** A booking id to offer Release for straight away (the reminder's deep link). */
  releaseId: {type: String, default: null},
  /** A booking id to open Pay now for as soon as the list is up (the match page's link, the unpaid email), R4 NH1. */
  payId: {type: String, default: null},
  /** Keep the list to this many upcoming rows (the club page card). */
  limit: {type: Number, default: 0},
  /** The line under the list (the viewer's allowance). */
  footLine: {type: String, default: null},
  /** Only the viewer's own bookings: a club booking an admin made is the club's, not theirs (R21 N9; the club page). */
  personal: {type: Boolean, default: false},
  /** No card of its own: the list sits edge to edge inside a flush dialog, rows carrying the dividers (R22 Low 4). */
  bare: {type: Boolean, default: false},
});
const emit = defineEmits(['changed', 'loaded']);

const api = useBookingsMember();
const {setAlertMessage} = useAlertService();

const list = ref(null); // null = loading
const errors = ref({});
const showPast = ref(false);
const rowBusy = ref({});

const load = async () => {
  errors.value = {};
  try {
    list.value = await api.mine({clubId: props.clubId, includePast: props.withPast && showPast.value});
  } catch (e) {
    list.value = [];
    errors.value = {global: [bookingApiError(e, "Your bookings couldn't be loaded.")]};
  }
  emit('loaded', list.value);
};
onMounted(load);
watch(showPast, load);
watch(() => props.clubId, load);

const split = computed(() => splitBookings(props.personal ? personalBookings(list.value) : (list.value ?? [])));
const upcoming = computed(() => props.limit ? split.value.upcoming.slice(0, props.limit) : split.value.upcoming);
const past = computed(() => split.value.past);
const empty = computed(() => list.value && !upcoming.value.length && !(showPast.value && past.value.length));
const chip = (b) => bookingStatusChip(b);
const tag = (b) => bookingKindChip(b);
// R6 Low 5: a match row names its sides (the API's title), not just "Match".
const sub = (b) => [props.showClub ? b.clubName : null, b.title, withLine(b), paidWords(b)].filter(Boolean).join(' · ');
const isMatch = (b) => b.kind === 'Match' || b.kind === 'MeetHold';
const waitingOnCard = (b) => viewerOwes(b);

const setBusy = (id, on) => { rowBusy.value = {...rowBusy.value, [id]: on}; };
const act = async (b, fn, done) => {
  setBusy(b.id, true);
  errors.value = {};
  try {
    const res = await fn();
    setAlertMessage(res?.message || done);
    await load();
    emit('changed');
  } catch (e) {
    errors.value = {global: [bookingApiError(e)]};
  } finally {
    setBusy(b.id, false);
  }
};
const cancel = (b) => act(b, () => api.cancel(b.id), 'Booking cancelled.');
// The API's own message shows (it says Released / Cancelled itself); the words here are the fallback.
const release = (b) => act(b, () => api.release(b.id), 'Released. Someone else can have the slot.');

// ── Pay now (a booking still waiting on a card): BookingPayDialog ──
const payOpen = ref(false);
const payFor = ref(null);
const openPay = (b) => { payFor.value = b; payOpen.value = true; };
const afterPaid = async () => {
  await load();
  emit('changed');
};

// The reminder's deep link: the row is highlighted and Release is the obvious tap.
const highlighted = computed(() => props.releaseId ? String(props.releaseId).toLowerCase() : null);

// ?pay=<id>: open the pay dialog on that row once the list is loaded, on a direct load and on an in-app link alike.
// A row that is not waiting on a card (already paid, released) is left alone: the list itself says what happened.
const payOpened = ref(null);
const openPayFromLink = () => {
  if (!props.payId || !list.value || payOpened.value === props.payId) return;
  const wanted = String(props.payId).toLowerCase();
  const row = (list.value ?? []).find((b) => String(b.id).toLowerCase() === wanted);
  payOpened.value = props.payId;
  if (row && waitingOnCard(row)) openPay(row);
};
watch([() => props.payId, list], () => openPayFromLink());
defineExpose({reload: load});
</script>

<template>
  <div class="mbl">
    <div v-if="list === null" class="mbl-loading" :class="{'tl-card': !bare}"><v-progress-circular indeterminate size="22" width="2" color="primary"/></div>
    <template v-else>
      <crud-error-display class="mb-3" :class="{'mx-4 mt-4': bare}" :errors="errors"/>

      <div v-if="empty" class="mbl-empty" :class="{'tl-card': !bare}">
        <v-icon size="32" color="primary" :icon="BOOKING_ICON"/>
        <div class="mbl-empty-title">No bookings coming up</div>
        <!-- Grant 2026-11-02: every club here, so no club's noun to say: no "playing area" -->
        <div class="mbl-empty-sub">Book from a club's page.</div>
      </div>

      <template v-if="upcoming.length">
        <div v-if="withPast" class="tl-label mbl-label">Coming up</div>
        <div class="mbl-list" :class="{'tl-card': !bare}">
          <div v-for="b in upcoming" :key="b.id" class="mbl-row" :class="{'mbl-row--hi': highlighted && b.id.toLowerCase() === highlighted}">
            <!-- Grant 2026-11-02: the row is the way to the booking's own page -->
            <nuxt-link :to="bookingDetailPath(b)" class="mbl-main" :aria-label="'Open booking: ' + b.line">
              <div class="mbl-ic" :class="{'mbl-ic--match': isMatch(b)}"><v-icon size="18" :icon="isMatch(b) ? 'mdi-sword-cross' : BOOKING_ICON"/></div>
              <div class="flex-fill min-w-0">
                <div class="mbl-title">
                  {{ b.line }}
                  <span v-if="tag(b)" class="tl-chip tl-chip--ok mbl-chip">{{ tag(b) }}</span>
                  <span v-if="b.status !== 'Confirmed'" class="tl-chip mbl-chip" :class="chip(b).cls">{{ chip(b).label }}</span>
                </div>
                <div v-if="sub(b)" class="mbl-sub">{{ sub(b) }}</div>
                <div v-if="b.shareStatusLine" class="mbl-sub">{{ b.shareStatusLine }}</div>
                <div v-if="b.cancelLine && b.canCancel" class="mbl-sub">{{ b.cancelLine }}</div>
              </div>
            </nuxt-link>
            <div class="mbl-actions">
              <v-btn v-if="waitingOnCard(b)" color="primary" rounded="pill" size="small" :loading="rowBusy[b.id]" @click="openPay(b)">Pay now</v-btn>
              <crud-confirm-dialog v-if="b.canRelease && (highlighted === b.id.toLowerCase() || !b.canCancel)" :title="releaseWords(b).title" :message="releaseWords(b).message" :confirm-text="releaseWords(b).confirmText" cancel-text="Keep it" @confirm="release(b)">
                <template #default="{click}">
                  <v-btn variant="outlined" color="error" rounded="pill" size="small" :loading="rowBusy[b.id]" @click="click">Release</v-btn>
                </template>
              </crud-confirm-dialog>
              <crud-confirm-dialog v-else-if="b.canCancel" :title="cancelWords(b).title" :message="cancelWords(b).message" :confirm-text="cancelWords(b).confirmText" cancel-text="Keep it" @confirm="cancel(b)">
                <template #default="{click}">
                  <v-btn variant="outlined" color="error" rounded="pill" size="small" :loading="rowBusy[b.id]" @click="click">Cancel</v-btn>
                </template>
              </crud-confirm-dialog>
              <span v-else-if="isMatch(b)" class="mbl-hint">From the match</span>
            </div>
          </div>
        </div>
      </template>
      <p v-if="footLine && upcoming.length" class="mbl-foot">{{ footLine }}</p>

      <template v-if="withPast">
        <button v-if="!showPast" type="button" class="mbl-link" @click="showPast = true">Show past bookings</button>
        <template v-else-if="past.length">
          <div class="tl-label mbl-label">Past</div>
          <div class="tl-card mbl-list">
            <div v-for="b in past" :key="b.id" class="mbl-row mbl-row--past">
              <nuxt-link :to="bookingDetailPath(b)" class="mbl-main" :aria-label="'Open booking: ' + b.line">
                <div class="mbl-ic mbl-ic--past"><v-icon size="18" :icon="isMatch(b) ? 'mdi-sword-cross' : BOOKING_ICON"/></div>
                <div class="flex-fill min-w-0">
                  <div class="mbl-title">{{ b.line }}<span v-if="tag(b)" class="tl-chip mbl-chip">{{ tag(b) }}</span></div>
                  <div class="mbl-sub">{{ [props.showClub ? b.clubName : null, chip(b).label, b.refundedMinor > 0 ? `refunded ${money(b.refundedMinor, b.currency)}` : null].filter(Boolean).join(' · ') }}</div>
                </div>
              </nuxt-link>
            </div>
          </div>
        </template>
        <div v-else class="mbl-sub mbl-none">No past bookings.</div>
      </template>
    </template>

    <booking-pay-dialog v-model="payOpen" :booking="payFor" @paid="afterPaid"/>
  </div>
</template>

<style scoped lang="scss">
.mbl-loading { display: flex; justify-content: center; padding: 26px; }
.mbl-label { margin: 14px 0 6px; }
.mbl-empty { text-align: center; padding: 28px 20px; }
.mbl-empty-title { font-size: 14.5px; font-weight: 700; margin-top: 6px; }
.mbl-empty-sub { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 3px; }
.mbl-list { padding: 0; overflow: hidden; }
.mbl-row {
  display: flex; align-items: center; gap: 12px; padding: 12px 14px; border-top: 1px solid rgb(var(--v-theme-borderLight));
  &:first-child { border-top: 0; }
  &--hi { background: rgb(var(--v-theme-brandTint)); }
  &--past { opacity: .8; }
}
.mbl-main {
  display: flex; align-items: center; gap: 12px; flex: 1; min-width: 0; color: inherit; text-decoration: none; border-radius: 10px;
  &:hover .mbl-title, &:focus-visible .mbl-title { color: rgb(var(--v-theme-primary)); }
}
.mbl-ic {
  width: 38px; height: 38px; border-radius: 11px; flex-shrink: 0; display: flex; align-items: center; justify-content: center;
  background: rgb(var(--v-theme-brandTint)); color: rgb(var(--v-theme-primary));
  &--match { background: rgb(var(--v-theme-successTint)); color: rgb(var(--v-theme-success)); }
  &--past { background: rgb(var(--v-theme-light)); color: rgb(var(--v-theme-charcoal)); }
}
.mbl-title { font-size: 13.5px; font-weight: 700; letter-spacing: -0.2px; display: flex; align-items: center; gap: 6px; flex-wrap: wrap; }
.mbl-chip { font-size: 10.5px; }
.mbl-sub { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; line-height: 1.4; }
.mbl-none { padding: 8px 2px; }
.mbl-actions { display: flex; gap: 6px; align-items: center; flex-shrink: 0; }
.mbl-hint { font-size: 11.5px; color: rgb(var(--v-theme-textSubtle)); }
.mbl-foot { font-size: 12px; color: rgb(var(--v-theme-textSubtle)); line-height: 1.5; margin: 8px 2px 0; }
.mbl-link { border: 0; background: none; padding: 10px 2px 0; color: rgb(var(--v-theme-primary)); font-size: 12.5px; font-weight: 600; cursor: pointer; }
</style>
